import type { PanelProduct } from '@storeo/api-client';
import type { StorefrontCatalogItem, StorefrontStore } from '@storeo/api-client';
import { LUMINA_TEMPLATES } from '@storeo/templates';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080';

export interface PreviewSection {
  key: string;
  visible: boolean;
}

export interface PreviewDraft {
  name: string;
  tagline: string;
  primaryColor: string;
  accentColor: string;
  logoUrl: string | null;
  sections: PreviewSection[];
  testimonialQuote: string;
  testimonialAuthor: string;
  template: string;
}

function toStore(draft: PreviewDraft, subdomain: string): StorefrontStore {
  return {
    name: draft.name || 'Sua loja',
    subdomain,
    tagline: draft.tagline || null,
    template: draft.template,
    primaryColor: draft.primaryColor,
    accentColor: draft.accentColor,
    customerAccessMode: 'password',
    logoUrl: draft.logoUrl,
    sections: draft.sections,
    testimonialQuote: draft.testimonialQuote || null,
    testimonialAuthor: draft.testimonialAuthor || null,
  };
}

function toCatalogItem(p: PanelProduct): StorefrontCatalogItem {
  return {
    name: p.name,
    slug: p.slug,
    description: p.description ?? '',
    type: p.type,
    amount: p.amount,
    deliveryChannel: p.deliveryChannel,
    featured: p.featured,
  };
}

/** Renderiza o MESMO componente do storefront real (`@storeo/templates`) — não é uma 2ª
 *  implementação à parte, então o Studio nunca mais diverge do que o comprador vê de verdade.
 *
 *  Entrega 1: o template Lumina Cinema é réplica fiel e verbatim do protótipo (`lpplat/`),
 *  então `draft`/`products` ainda não são consumidos pelo template — a costura de dados reais
 *  vem numa entrega seguinte. `toStore`/`toCatalogItem` seguem aqui pra essa retomada. */
export function TemplatePreview({
  draft,
  products,
  subdomain,
  view,
}: {
  draft: PreviewDraft;
  products: PanelProduct[];
  subdomain: string;
  view: 'vitrine' | 'produto';
}) {
  const store = toStore(draft, subdomain);
  const items = products.filter((p) => p.status === 'published').map(toCatalogItem);
  const { Catalog, Product } =
    LUMINA_TEMPLATES[draft.template as keyof typeof LUMINA_TEMPLATES] ??
    LUMINA_TEMPLATES['lumina-cinema'];

  const style = {
    '--sf-primary': draft.primaryColor,
    '--sf-accent': draft.accentColor,
  } as React.CSSProperties;

  const featured = items.find((p) => p.featured) ?? items[0] ?? null;

  return (
    <div style={style}>
      {view === 'vitrine' ? (
        <Catalog data={{ store, products: items }} apiBase={API_BASE} />
      ) : (
        <Product
          data={{ store, product: featured ?? PLACEHOLDER_PRODUCT }}
          apiBase={API_BASE}
          relatedProducts={items}
        />
      )}
    </div>
  );
}

const PLACEHOLDER_PRODUCT: StorefrontCatalogItem = {
  name: 'Produto',
  slug: 'produto',
  description: '',
  type: 'one_time',
  amount: 0,
  deliveryChannel: 'digital_file',
  featured: false,
};
